/**
 * Help texts of the admin finance pages — one data file (fleet
 * frontend-standards §6): what it means, which direction is good, and a good
 * range ONLY where a defensible general reference exists (otherwise it says
 * so and names what to compare against). No formulas of the payment engine,
 * no thresholds that are not documented. Hungarian: the pages are Hungarian.
 */
export interface HelpEntry {
  what: string
  direction: string
  range: string
}

const NO_RANGE = 'Nincs általános jó tartomány – a saját korábbi értékedhez és a többi projekthez érdemes hasonlítani.'

export const HELP: Record<string, HelpEntry> = {
  revenue: {
    what: 'A kiválasztott időszakban sikeresen lezárult (finished) fizetési tranzakciók összege, pénznemenként külön. Az egyenlegből fizetett megújítás nem számít bevételnek itt: a készpénz a kredit vásárlásakor már elszámolódott. A coinban könyvelt kredit-befizetések a kredit-táblában vannak.',
    direction: 'Magasabb a jobb. A mellette lévő százalék az előző, azonos hosszúságú időszakhoz viszonyít.',
    range: NO_RANGE,
  },
  mrr: {
    what: 'Az aktív előfizetések havi díjának összege (havi ismétlődő bevétel), pénznemenként. Ennyi bevétel várható havonta, ha minden megújítás sikerül.',
    direction: 'Növekedés jó; a csökkenés lemondást vagy lejáró előfizetést jelez.',
    range: NO_RANGE,
  },
  subscriptions: {
    what: 'Az aktív előfizetések darabszáma. Mellette látszik, hány új jött és hány szűnt meg az időszakban – a nettó szám önmagában elrejtené a lemorzsolódást.',
    direction: 'Több új és kevesebb megszűnt a jobb.',
    range: NO_RANGE,
  },
  failed: {
    what: 'Elutasított vagy visszautasított ütemezett terhelések (megújítás, feltöltés) az elmúlt 30 napban: darabszám és összeg együtt, mert a „0 sikertelen” csak akkor jó, ha volt terhelés.',
    direction: 'Kevesebb a jobb.',
    range: NO_RANGE,
  },
  unknown: {
    what: 'Olyan terhelés, amelynek a Stripe-oldali eredménye nem ismert (például időtúllépés). Amíg ember nem egyezteti, a rendszer szándékosan nem próbálja újra, hogy ne terheljen kétszer.',
    direction: 'Alacsonyabb a jobb.',
    range: 'Az elvárt érték 0: minden ismeretlen kimenetű terhelést ember kell, hogy rendezzen.',
  },
  overdue: {
    what: 'A tartozó (3-as státuszú) előfizetések kifizetetlen díja. Ha az újrapróbálások kimerülnek, az előfizetés inaktiválódik.',
    direction: 'Kevesebb a jobb.',
    range: NO_RANGE,
  },
  monthlyChart: {
    what: 'Havonta befolyt, sikeresen lezárult bevétel projektenként egymásra halmozva, egy pénznemben. A legutolsó (folyamatban lévő) hónap részleges, ezért halványabb.',
    direction: 'Magasabb oszlop a jobb; a rétegek a projektek hozzájárulását mutatják. A jelmagyarázat elemére kattintva a projekt ki- és bekapcsolható.',
    range: NO_RANGE,
  },
  sources: {
    what: 'Honnan származik a bevétel az időszakban: előfizetés, egyszeri termék, kredit-feltöltés, használati díj (havi zárás) vagy egyéb. Pénznemenként külön csoport.',
    direction: 'Attól függ: az előfizetés ismétlődő, a többi esetenként. A forrás-arány változása a tanulságos.',
    range: NO_RANGE,
  },
  projectsTable: {
    what: 'Projektenként a bevétel, a havi ismétlődő bevétel, az előfizetések és a nyitott gondok. Az oszlopfejlécre kattintva rendezhető.',
    direction: 'Oszloponként eltérő – lásd az oszlopok súgóját.',
    range: NO_RANGE,
  },
  colRevenue: {
    what: 'Az időszak sikeres bevétele a projektben, pénznemenként. A „vs. előző” oszlop a megelőző azonos hosszú időszakhoz viszonyít.',
    direction: 'Magasabb a jobb.',
    range: NO_RANGE,
  },
  colClose: {
    what: 'A legutóbbi lezárt havi használati díj-zárás hónapja. „Zárás elmaradt”: az előző hónapban volt használati díj, de nincs lezárt blokk.',
    direction: 'Az előző hónap zárása legyen meg.',
    range: 'Elvárt: az előző naptári hónap zárása megvan.',
  },
  queue: {
    what: 'A legsúlyosabb nyitott tételek (kritikus, figyelem, megelőzhető) súlyosság szerint. A teljes lista a „Figyelmet igényel” oldalon van.',
    direction: 'Kevesebb tétel a jobb; a kritikus tétel a legsürgősebb.',
    range: 'Elvárt: nincs kritikus tétel.',
  },
  upcoming: {
    what: 'A következő napokban esedékes előfizetés-megújítások darabszáma (az ütemezett kredit-feltöltések összege előre nem határozható meg, ezért nincsenek benne) és bruttó összege, napokra bontva, pénznemenként. Az „egyenlegből” rész az, amit a számla egyenlegei fedeznének, a „kártyára” a maradék – a mai egyenlegekből számolt előrejelzés, a tényleges döntést a terhelés pillanatában hozza a rendszer.',
    direction: 'Tájékoztató előrejelzés; a sikeres terhelések aránya a megújítási kísérletektől függ.',
    range: NO_RANGE,
  },
  credits: {
    what: 'A felhasználók kredit-egyenlege pénznemenként: összes egyenleg, számlák száma és a negatív egyenlegű számlák száma. Minden pénznem külön – nincs átváltás, nincs közös összeg.',
    direction: 'A negatív számlák száma legyen alacsony.',
    range: 'Elvárt: nincs negatív egyenlegű számla.',
  },
  atRisk: {
    what: 'A nyitott problémák mögötti, még rendezetlen bruttó összeg pénznemenként. Alatta látszik, ebből mennyit fedeznének a számlák egyenlegei (USDT, USDC, majd kredit, 1:1 USD); a többit kártyáról kellene behajtani.',
    direction: 'Alacsonyabb a jobb.',
    range: NO_RANGE,
  },
  severity: {
    what: 'Kritikus: ismeretlen kimenetű terhelés, ember dönt. Figyelem: elutasított kártya, tartozás, negatív kredit, elmaradt zárás. Megelőzhető: még nem hiba, de a következő megújítást veszélyezteti.',
    direction: 'Mindhárom csoportban kevesebb a jobb; a sorrend a sürgősséget követi.',
    range: 'Elvárt: nincs kritikus tétel.',
  },
  attempt: {
    what: 'Hányadik próbálkozás a megengedett számból. Kimerült próbálkozásoknál az előfizetés a megadott időpontban inaktiválódik.',
    direction: 'Alacsonyabb a jobb.',
    range: NO_RANGE,
  },
  lifetime: {
    what: 'A felhasználó összes sikeres befizetése, pénznemenként.',
    direction: 'Magasabb a jobb.',
    range: NO_RANGE,
  },
  balances: {
    what: 'A felhasználó kredit-egyenlege pénznemenként.',
    direction: 'Nem negatív egyenleg a jó.',
    range: 'Elvárt: nem negatív.',
  },
  timeline: {
    what: 'A felhasználó pénzügyi eseményei időrendben (terhelések, befizetések, kredit, előfizetés, admin-műveletek), legfrissebb elöl.',
    direction: 'Nincs „jó irány”; a sorozatban ismétlődő elutasítás a figyelmeztető jel.',
    range: NO_RANGE,
  },
  stripeLog: {
    what: 'Minden Stripe-hívás egy sor: folyamat, eredmény, HTTP-állapot és a Stripe által adott kód. Kártyaadat nincs tárolva.',
    direction: 'Sikeres eredmény a jó; ismétlődő elutasítás-kód a probléma oka.',
    range: NO_RANGE,
  },
  adminOps: {
    what: 'A kézi admin-műveletek naplója: ki, mikor, mit és milyen paraméterekkel csinált. Pénzügyi rekord nem törlődik – a lezárás új állapotot ír.',
    direction: 'Nincs „jó irány”; a váratlan művelet az, amit érdemes megnézni.',
    range: NO_RANGE,
  },
  balanceRule: {
    what: 'A megújításokat és a feltöltéseket a rendszer először a számla egyenlegeiből fizeti: USDT, majd USDC, majd kredit, 1:1 USD-ben (csak pozitív egyenleg; BTC és ETH soha). Csak a maradék megy a kártyára.',
    direction: 'Az egyenlegből fizetett megújítás nem kártyaterhelés és nem új készpénz-bevétel; a kártyára menő rész az, ami elutasítható.',
    range: NO_RANGE,
  },
  coverFlag: {
    what: 'Ismeretlen kimenetű, részben egyenlegből fedezett terhelésnél a jelzés azt mondja meg, miért nem könyvelte az egyeztetés a sikeres terhelést: az egyenleg-fedezet hiányos, vagy a tervezett levonások nincsenek rögzítve.',
    direction: 'Nem lehet jelzés nélkül a jó; a jelzett tétel kézi egyeztetést kér.',
    range: 'Elvárt: nincs jelzett tétel.',
  },
}
